import { assign, createMachine, send, sendParent } from 'xstate';
import { choose } from 'xstate/lib/actions';
import AudacyLogger, { LoggerTopic } from '../../AudacyLogger';
import { PlayerAction, PlayerMetric, SaveResumePointTimings } from '../../Constants';
import Chapter from '../../dataServices/Chapter';
import { handleErrorAction, setQuickPauseChapterCache } from '../../utils';
import bufferingEventCounter from '../bufferingEventCounter';
import { type TStreamerMachineEvent, EContentType, type TPlayableObject } from '../types';
import {
  AUTO_TRANSITION,
  COMMON_ACTIONS,
  MAX_RETRY_ATTEMPTS,
  NON_REWINDABLE_ERROR,
  SCHEDULE_UPDATE_INTERVAL,
} from './constants';
import {
  type ILiveStationAudioSource,
  type IStreamerMachineContext,
  type TStreamSelector,
} from './types';
import {
  DEFAULT_PLAYER_ACTIONS,
  episodeCoversTimestamp,
  getCurrentChapterIndex,
  getExponentialBackoffDelay,
  getFirstAudioSourceUrl,
  getLiveChapter,
  getLoadParams,
  getPlayableDataObject,
  isContentTypeAd,
  loadSourcesForItem,
  mapPlayerTime,
  mergeMetadata,
  normalizeResumePoint,
  performSeek,
  saveResumePoint,
  saveResumePoints,
  type StreamType,
  toIntegerOrUndefined,
  reportPlaybackRequestedConviva,
  setContentInfoConviva,
} from './utils';

// Test station slugs, e.g. /stations/wfan, on STG backend:
// Normal stations: wfan, 94wip, 1010wins, etc.
// Non-rewindable test case: test670thescore
// Alternate rewind/non-rewind each hour: test1010wins
// Also recommend setting useHls (below and in HtmlPlayer.ts) to false to test non-HLS on desktop Safari

const { RESUME_POINT_INTERVAL_MSEC } = SaveResumePointTimings;

export type IRewindStationMachineContext = {
  streamType: StreamType;
  playLive?: boolean;
  seekingInChapter?: boolean;
  source?: ILiveStationAudioSource;
  url?: string;
  // [OOP-756] Arms the out-of-range requestedOffset guard in getSchedule for
  // the FIRST schedule resolution after a LOADING entry only. Set by
  // `assignDefaults`, cleared by getSchedule after one use, so the periodic
  // UPDATING_SCHEDULE poll never flips an in-progress session to live. Same
  // mechanism as MP3RewindStationMachine — see the doc-comment there.
  validateOffsetOnLoad?: boolean;
} & IStreamerMachineContext<TPlayableObject>;

export const REWIND_MACHINE_ID = 'rewindStationMachine';

type TRewindStationMachineServices = {
  getSchedule: {
    data: Awaited<IRewindStationMachineContext>;
  };
  getChapters: {
    data: Awaited<IRewindStationMachineContext>;
  };
  loadStationSources: {
    data: Awaited<IRewindStationMachineContext>;
  };
};

const rewindStationMachine = createMachine(
  {
    predictableActionArguments: true,
    id: 'rewindStationMachine',
    initial: 'LOADING',
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    tsTypes: {} as import('./RewindStationMachine.typegen').Typegen0,
    schema: {
      context: {
        timeSinceLastSavedResumePoint: Date.now(),
      } as IRewindStationMachineContext,
      events: {} as TStreamerMachineEvent,
      services: {} as TRewindStationMachineServices,
    },
    states: {
      LOADING: {
        id: 'loading',
        entry: 'assignDefaults',
        initial: 'LOADING_SOURCES',
        states: {
          LOADING_SOURCES: {
            invoke: {
              id: 'loadStationSources',
              src: 'loadStationSources',
              onDone: {
                actions: ['assignRewindContext'],
                target: 'LOADING_SCHEDULE',
              },
              onError: {
                actions: [
                  'sendPlaybackFailedToLoadEvent',
                  handleErrorAction('LOADING_SOURCES', 'rewindStationMachine'),
                ],
                target: 'FAILED_TO_LOAD',
              },
            },
          },
          LOADING_SCHEDULE: {
            invoke: {
              id: 'getSchedule',
              src: 'getSchedule',
              onDone: {
                actions: [
                  'assignScheduleResponse',
                  'assignMetadata',
                  'assignCurrentChapterIndexWithOffset',
                  'loadAudio',
                  'clearPlayLive',
                ],
              },
              onError: {
                actions: [
                  'sendPlaybackFailedToLoadEvent',
                  handleErrorAction('LOADING_SCHEDULE', 'rewindStationMachine'),
                ],
                target: 'FAILED_TO_LOAD',
              },
            },
          },
          FAILED_TO_LOAD: {
            entry: ['logLoadingError', 'assignTrackError'],
            after: [
              {
                delay: AUTO_TRANSITION,
                target: '#failureWithRetry',
              },
            ],
          },
        },
        on: {
          REPORT_LOADED: {
            target: 'LOADED',
          },
          REPORT_METADATA: {
            actions: 'assignMetadata',
          },
          PAUSE: {
            target: 'PAUSED',
            actions: ['stop'],
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
      },
      LOADED: {
        entry: 'autoplay',
        on: {
          PLAY: {
            target: 'PLAY_REQUESTED',
          },
          REPORT_METADATA: {
            actions: 'assignMetadata',
          },
          // Destroy stream when autoplay is false to stop infinite HLS buffering
          // Go straight to PAUSED because the audio was never loaded in the first place on mobile
          PAUSE: {
            target: 'PAUSED',
            actions: ['stop'],
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
      },
      PLAY_REQUESTED: {
        id: 'playRequested',
        entry: 'playWithOffset',
        on: {
          REPORT_PLAYING: {
            actions: ['sendPlayEvent', 'addToHistory'],
            target: 'PLAYING',
          },
          REPORT_METADATA: {
            actions: 'assignMetadata',
          },
          REPORT_AUTOPLAY_POLICY: {
            target: 'PAUSED',
          },
          PAUSE: {
            target: 'PAUSED',
            actions: ['stop'],
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
      },
      TRANSITION_TO_BUFFERING: {
        entry: ['sendBufferingEvent'],
        on: {
          REPORT_PLAYING: {
            actions: ['sendResumeFromBufferingEvent'],
            target: 'PLAYING',
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
        after: [
          {
            // When android buffers data, it will enter a loading state and a pause state directly after.
            // This will cause the player to pause the stream. This will ignore that pause event then let the user pause manually
            target: 'BUFFERING',
            delay: 100,
          },
        ],
      },
      BUFFERING: {
        on: {
          PAUSE: {
            target: 'PAUSE_REQUESTED',
          },
          REPORT_LOADING: {
            target: 'TRANSITION_TO_BUFFERING',
          },
          REPORT_PAUSED: {
            target: 'PAUSED',
          },
          REPORT_PLAYING: {
            actions: ['sendResumeFromBufferingEvent'],
            target: 'PLAYING',
          },
          REPORT_METADATA: {
            actions: ['sendLiveSongListenedEvent', 'assignMetadata'],
          },
          STOP: {
            target: 'PAUSED',
            actions: ['stop', 'sendStopEvent'],
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
      },
      PLAYING: {
        initial: 'IS_PLAYING',
        entry: 'assignRetryCountToZero',
        states: {
          IS_PLAYING: {
            on: {
              REPORT_METADATA: {
                actions: ['assignMetadata', 'clearPlayLive'],
                target: 'UPDATING_CHAPTERS',
              },
              REPORT_TIME_UPDATE: [
                {
                  cond: 'isFinishedStandaloneChapter',
                  actions: ['assignTime', 'saveResumePoint', 'resetElapsed', 'sendReportEnded'],
                },
                {
                  cond: 'isPlayingAfterEntityEnd',
                  actions: ['assignTime', 'saveResumePoint', 'resetElapsed'],
                  target: 'UPDATING_SCHEDULE',
                },

                {
                  target: 'IS_PLAYING',
                  actions: ['assignTime', 'assignCurrentChapterIndex', 'saveLocalResumePoint'],
                },
              ],
              REPORT_PLAYBACK_FAILED: {
                target: '#failureWithRetry',
                actions: 'sendNativePlaybackFailedEvent',
              },
            },
            after: [
              {
                delay: SCHEDULE_UPDATE_INTERVAL,
                target: 'UPDATING_SCHEDULE',
              },
            ],
          },
          UPDATING_SCHEDULE: {
            invoke: {
              id: 'getSchedule',
              src: 'getSchedule',
              onDone: [
                {
                  cond: 'isNonRewindableContent',
                  actions: [
                    'stop',
                    'exitRewindMode',
                    'setPlayLive',
                    handleErrorAction('UPDATING_SCHEDULE', 'rewindStationMachine'),
                  ],
                  target: '#loading',
                },
                {
                  actions: [
                    'assignScheduleResponse',
                    'assignMetadata',
                    'addEpisodeToHistory',
                    'assignCurrentChapterIndex',
                  ],
                  target: 'IS_PLAYING',
                },
              ],
              onError: [
                {
                  cond: 'isNonRewindableContentError',
                  actions: [
                    'stop',
                    'exitRewindMode',
                    'setPlayLive',
                    handleErrorAction('UPDATING_SCHEDULE', 'rewindStationMachine'),
                  ],
                  target: '#loading',
                },
                {
                  actions: [handleErrorAction('UPDATING_SCHEDULE', 'rewindStationMachine')],
                },
              ],
            },
          },
          UPDATING_CHAPTERS: {
            invoke: {
              id: 'getChapters',
              src: 'getChapters',
              onDone: {
                actions: ['assignChapterResponse', 'assignCurrentChapterIndex'],
                target: 'IS_PLAYING',
              },
              onError: {
                // Log the error but continue playing without chapters
                actions: [handleErrorAction('UPDATING_CHAPTERS', 'rewindStationMachine')],
                target: 'IS_PLAYING', // ← Continue playing even if chapters fail
              },
            },
          },
        },
        on: {
          GO_TO_LIVE: {
            actions: ['stop', 'exitRewindMode', 'setPlayLive'],
            target: 'LOADING',
          },
          SEEK_TIME: [
            {
              cond: 'hasSeekedToEndOfStandaloneChapter',
              actions: ['stop', 'resetResumePoint', 'handleRewindContentEnded'],
              target: 'PAUSED',
            },
            {
              actions: ['stop', 'assignSeekTime', 'saveResumePoint'],
              target: 'LOADING',
            },
          ],
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_LOADING: {
            target: 'TRANSITION_TO_BUFFERING',
          },
          REPORT_ENDED: [
            {
              cond: 'isStandaloneChapter',
              actions: ['stop', 'handleRewindContentEnded', 'sendStopEvent'],
              target: 'PAUSED',
            },
            {
              cond: 'isPlayingRewindContent',
              actions: ['stop', 'handleRewindContentEnded', 'sendStopEvent'],
              target: 'LOADING',
            },
          ],
          REPORT_CONTINUE_PLAYING: [
            {
              cond: 'shouldStandaloneChapterStop',
              actions: ['stop', 'resetResumePoint', 'handleRewindContentEnded'],
              target: 'PAUSED',
            },
            {
              target: 'LOADING',
              actions: ['setIsLiveAndRequestedOffset'],
            },
          ],
          REPORT_PAUSED: [
            {
              cond: 'isStandaloneChapter',
              actions: ['handleRewindContentEnded', 'sendStopEvent'],
              target: 'PAUSED',
            },
            {
              target: 'PAUSED',
            },
          ],
          PAUSE: {
            target: 'PAUSE_REQUESTED',
          },
          STOP: {
            target: 'PAUSE_REQUESTED',
          },
          REPORT_TIME_UPDATE: [
            {
              cond: 'isPlayingAfterEntityEnd',
              actions: 'sendReportEnded',
            },
            {
              actions: 'assignTime',
            },
          ],
          REPORT_METADATA: {
            actions: ['assignMetadata', 'clearPlayLive'],
          },
          ...COMMON_ACTIONS,
        },
      },
      PAUSE_REQUESTED: {
        entry: choose([
          {
            cond: 'isAd',
            actions: 'pause',
          },
          {
            cond: 'isRewindable',
            actions: ['saveResumePoint', 'cacheChaptersOnPause', 'stop'],
          },
          {
            actions: 'stop',
          },
        ]),
        on: {
          ...COMMON_ACTIONS,
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PAUSED: {
            target: 'PAUSED',
          },
        },
      },
      PAUSED: {
        entry: ['sendStopEvent', 'assignRetryCountToZero'],
        on: {
          GO_TO_LIVE: {
            actions: ['stop', 'exitRewindMode', 'setPlayLive'],
            target: 'LOADING',
          },
          SEEK_TIME: [
            {
              cond: 'hasSeekedToEndOfStandaloneChapter',
              actions: ['stop', 'resetResumePoint', 'handleRewindContentEnded'],
              target: 'PAUSED',
            },
            {
              actions: ['stop', 'assignSeekTime', 'saveResumePoint'],
              target: 'LOADING',
            },
          ],
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYING: {
            target: 'PLAYING',
          },
          PLAY: [
            {
              cond: 'isAd',
              target: 'PLAY_REQUESTED',
            },
            {
              target: 'LOADING',
            },
          ],
          ...COMMON_ACTIONS,
        },
      },
      ENDED: {
        on: {
          GO_TO_LIVE: {
            actions: ['stop', 'exitRewindMode', 'setPlayLive'],
            target: 'LOADING',
          },
          SEEK_TIME: [
            {
              cond: 'hasSeekedToEndOfStandaloneChapter',
              actions: ['stop', 'resetResumePoint', 'handleRewindContentEnded'],
              target: 'PAUSED',
            },
            {
              actions: ['stop', 'assignSeekTime', 'saveResumePoint'],
              target: 'LOADING',
            },
          ],
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
      },
      FAILURE_WITH_RETRY: {
        id: 'failureWithRetry',
        entry: ['assignIncrementRetryCount', 'logRetry'],
        on: {
          ...COMMON_ACTIONS,
        },
        after: [
          {
            // iOS will immediately send a pause event after a failure. This will cancel the retry attempt.
            // The FAILURE_WITH_RETRY state needs to ignore the Pause event and we quickly transition to
            // WAITING_TO_RETRY so the user can pause the stream.
            target: 'WAITING_TO_RETRY',
            delay: 100,
          },
        ],
      },
      WAITING_TO_RETRY: {
        on: {
          PLAY: {
            target: 'LOADING',
          },
          PAUSE: {
            target: 'PAUSED',
            actions: 'pause',
          },
          STOP: {
            actions: ['stop', 'sendStopEvent'],
          },
          REPORT_PAUSED: {
            target: 'PAUSED',
          },
          ...COMMON_ACTIONS,
        },
        after: [
          {
            target: 'FAILURE',
            delay: (context) => getExponentialBackoffDelay(context.retryCount),
            cond: ({ retryCount = 0 }) => retryCount >= MAX_RETRY_ATTEMPTS,
          },
          {
            actions: ['stop', 'loadAudio', sendParent({ type: 'PLAY' })],
            target: 'LOADING',
            delay: (context) => getExponentialBackoffDelay(context.retryCount),
            cond: ({ retryCount = 0 }) => retryCount < MAX_RETRY_ATTEMPTS,
          },
        ],
      },
      FAILURE: {
        id: 'failure',
        entry: ['logPlaybackFailure', 'stop', 'sendPlaybackFailedEvent'],
        on: {
          ...COMMON_ACTIONS,
        },
        after: [
          {
            delay: AUTO_TRANSITION,
            target: 'PAUSE_REQUESTED',
          },
        ],
      },
      DESTROYED: {
        entry: ['stop', 'sendStopEvent', 'saveResumePoint'],
        type: 'final',
      },
    },
  },
  {
    services: {
      getChapters: async function (ctx) {
        const { episode } = ctx;
        if (!episode) {
          return ctx;
        }

        const chapters =
          (await episode.getChapters({
            broadcastChapters: ctx.featureFlags?.broadcastChapters,
          })) ?? [];
        chapters.push(getLiveChapter(episode, chapters as Array<Chapter>));

        ctx.chapters = chapters;
        return ctx;
      },
      getSchedule: async function (ctx) {
        const { playLive } = ctx;
        let playbacks = await ctx.personalizationProvider.getPlaybacks();

        // 'Back to live' feature (BROADCAST only)
        if (playLive) {
          ctx.requestedOffset = undefined;
          playbacks = {};
        }

        // Get the episode (BROADCAST only)
        if (ctx.streamType === 'BROADCAST') {
          const schedules = await ctx.station?.getSchedules();

          // Case 1: no episode yet (initial load / resume) — resolve it from
          // the requestedOffset. Mid-session re-entries that KEEP the episode
          // (seeks, schedule refreshes) skip the resolution AND the coverage
          // guard below, mirroring the MP3 machine — pre-gate, every user
          // seek re-armed and re-ran validation here.
          if (ctx.requestedOffset && !ctx.episode) {
            const requestedOffset = ctx.requestedOffset;
            ctx.episode = await schedules?.getEpisode(requestedOffset * 1000);

            // [OOP-756] EpisodeList.getEpisode silently returns the schedule's
            // LAST episode when no entry covers the timestamp, so a stale or
            // future offset (deeplink, corrupted resume point) would start
            // playback at the wrong show. Reject uncovered offsets on the
            // initial load and jump to live instead. One-shot gate — see the
            // validateOffsetOnLoad doc-comment and the identical guard in
            // MP3RewindStationMachine (incl. the isRewindable alignment with
            // the deeplink validators).
            if (
              ctx.validateOffsetOnLoad &&
              (!episodeCoversTimestamp(ctx.episode, requestedOffset) ||
                !ctx.episode?.isRewindable())
            ) {
              ctx.logger?.info(
                `[OOP-756] requestedOffset ${requestedOffset} is outside the available schedule — jumping to live`,
              );
              // [OOP-731] Deeplink-quality observability — same metric as the
              // MP3 machine so flag-off (SSR) sessions are visible too.
              ctx.analyticsProvider.sendEventToListener({
                type: PlayerMetric.STREAM_OFFSET_OUT_OF_COVERAGE,
                eventDetails: {
                  stationId: ctx.item.getId(),
                  stationName: ctx.item.getTitle(),
                  streamerType: ctx.contentType,
                  currentPosition: requestedOffset,
                },
              });
              ctx.requestedOffset = undefined;
              ctx.episode = undefined;
              ctx.playLive = true;
              playbacks = {};
            }
          }

          ctx.validateOffsetOnLoad = false;

          // Case 2: we don't have an episode, default to live episode
          if (!ctx.episode) {
            ctx.episode = await schedules?.getEpisode(Date.now());
          }

          // Case 3: we already have an episode but no requestedOffset, do nothing
          // no-op
        }

        const isStandaloneChapter = ctx.streamType === 'STANDALONE_CHAPTER';

        const entity = isStandaloneChapter ? ctx.standaloneChapter : ctx.episode;

        // We should always have an episode now, this is just for typechecking (no-op)
        if (!entity) {
          // const message =  `BUG: No ${isStandaloneChapter ? 'standaloneChapter' : 'episode'} found while playing SSR`;
          // TODO: send message error to future logging service
          return ctx;
        }

        // Normalize the resume point to something we can use
        ctx.requestedOffset = normalizeResumePoint({
          resumePoint: ctx.requestedOffset ?? playbacks[entity.getId()],
          entity,
          streamType: ctx.streamType,
        });

        // const message =  `Resume point for entity ${entity.getId()} for SSR is ${ctx.requestedOffset}`;
        // TODO: send message error to future logging service

        // Throw an error if trying to play non-rewindable, non-live content (like a game)
        // TODO: [CCS-1762] this check should be part of loadAudio but that is currently an action not a service. We should refactor that to be a service and update the machine accordingly.
        if (!entity.isRewindable() && entity.getEndTimeMillis() < Date.now()) {
          // TODO: send error to future logging service
          throw new Error(NON_REWINDABLE_ERROR);
        }

        // Update episode chapters
        if (ctx.episode) {
          const playback = playbacks[entity.getId()];
          const chapters =
            (await ctx.episode.getChapters({
              playback,
              broadcastChapters: ctx.featureFlags?.broadcastChapters,
            })) ?? [];
          if (!chapters.length) {
            const chapter = new Chapter({
              duration: ctx.episode.getDuration(),
              startOffset: ctx.episode.getStartTimeSeconds(),
              title: 'Part 1',
              transcript: ctx.episode.getTranscript(),
            });
            chapters.push(chapter);
          }
          ctx.chapters = chapters;
        }

        return ctx;
      },
      loadStationSources: async function (ctx) {
        const source = (
          await loadSourcesForItem({
            ...ctx,
            // There is an edge case when playing a past BROADCAST rewind episode
            // that episode has a null streamUrl so use the station's streamUrl
            item: ctx.streamType === 'BROADCAST' ? (ctx.station as TPlayableObject) : ctx.item,
          })
        )?.find((p) => p.type === 'm3u8');

        return {
          ...ctx,
          source,
        };
      },
    },
    guards: {
      // Check if playing non-live content
      isPlayingRewindContent: ({ metadata }) => {
        return !metadata?.isLive;
      },
      isFinishedStandaloneChapter: (ctx) => {
        const { episode, elapsed, standaloneChapter } = ctx;
        const entity = standaloneChapter ?? episode;
        const isPlayingAfterEntityEnd = (elapsed ?? 0) > (entity?.getDuration() ?? 0);
        const standalone = !!ctx.standaloneChapter;

        return standalone && isPlayingAfterEntityEnd;
      },
      isPlayingAfterEntityEnd: (ctx) => {
        const { episode, elapsed, standaloneChapter } = ctx;
        const entity = standaloneChapter ?? episode;
        const isPlayingAfterEntityEnd = (elapsed ?? 0) > (entity?.getDuration() ?? 0);
        return isPlayingAfterEntityEnd;
      },
      isAd: ({ metadata }) => isContentTypeAd(metadata?.contentType),
      // TODO: Validate that this is actually right to default to true - might be better to switch over streamType
      isRewindable: ({ episode }) => episode?.isRewindable() ?? true,
      isNonRewindableContent: (ctx) => {
        return ctx.contentType === EContentType.Rewind && !ctx.episode?.isRewindable();
      },
      isNonRewindableContentError: (_, event) => {
        const { message } = event.data as Error;
        return message === NON_REWINDABLE_ERROR;
      },
      isStandaloneChapter: (ctx) => !!ctx.standaloneChapter,
      shouldStandaloneChapterStop: (ctx) => {
        // when a standalone chapter ends the player still attempts a tarzan swing
        // preroll ads can exist, so we want to make sure we only stop if the contentType is content.
        // beyond that a standaloneChapter and requestedOffset exiting tell us that we want to stop.
        const { metadata, requestedOffset, standaloneChapter } = ctx;
        return !!standaloneChapter && !!requestedOffset && metadata?.contentType === 'content';
      },
      hasSeekedToEndOfStandaloneChapter: (ctx, { time }) => {
        const { standaloneChapter } = ctx;
        
        if (!standaloneChapter) {
          return false;
        }

        const startTs = normalizeResumePoint({
          resumePoint: time,
          entity: standaloneChapter,
          streamType: 'STANDALONE_CHAPTER',
        });

        if (!startTs) {
          return false;
        }

        const endTs = standaloneChapter.getEndTimeSeconds();

        return startTs >= endTs;
      },
    },
    actions: {
      // Set default metadata so we have something to show while we load the audio
      assignDefaults: assign(({ item, metadata, firstLoadTime, analyticsProvider }) => {
        if (!metadata) {
          // Initial load
          analyticsProvider.sendEventToListener({
            type: PlayerMetric.STREAM_LOAD,
            eventDetails: {
              stationId: item.getId(),
              stationName: item.getTitle(),
              streamerType: EContentType.Rewind,
              isNativeErrorHandling: true,
            },
          });
          bufferingEventCounter.init(analyticsProvider, {
            stationId: item.getId(),
            stationName: item.getTitle(),
            streamerType: EContentType.Rewind,
          });
        }
        return {
          firstLoadTime: firstLoadTime || Date.now(),
          // [OOP-756] Arm the one-shot out-of-range offset guard for the
          // getSchedule call this LOADING pass is about to make.
          validateOffsetOnLoad: true,
          metadata: metadata ?? {
            image: item.getImageSquare(),
            station: item.getTitle(),
          },
        };
      }),
      assignRetryCountToZero: assign({
        retryCount: 0,
      }),
      assignIncrementRetryCount: assign({
        retryCount: ({ retryCount = 0 }) => retryCount + 1,
      }),
      // Add station and episode to history so both are accounted for in recents
      addToHistory: (ctx) => {
        const stationId = ctx.item.getId();
        const episodeId = ctx.episode?.getId();
        const standaloneChapterId = ctx.standaloneChapter?.getId();
        const contentIdsToAddToHistory = [];
        if (stationId) {
          contentIdsToAddToHistory.push(stationId);
        }
        if (episodeId) {
          contentIdsToAddToHistory.push(episodeId);
        }
        if (standaloneChapterId) {
          contentIdsToAddToHistory.push(standaloneChapterId);
        }

        // to avoid duplication of ids
        const uniqueContentIdsToAddToHistory = [...new Set(contentIdsToAddToHistory)];
        if (
          uniqueContentIdsToAddToHistory.length > 0 &&
          !ctx.personalizationProvider.isInTopOfHistoryStack(uniqueContentIdsToAddToHistory)
        ) {
          void ctx.personalizationProvider.addToHistory(uniqueContentIdsToAddToHistory);
        }
      },
      // [CCS-3193]: Type coercion used temporarily to fix issue
      // Come back and fix IRewindProviderContext
      // May need to remove the RewindProviderContext type from the IRewindProvider interface
      addEpisodeToHistory: (ctx, event) => {
        const data = event.data;
        const episodeId = data.episode?.getId();
        if (episodeId) {
          if (!ctx.personalizationProvider.isInTopOfHistoryStack(episodeId)) {
            void ctx.personalizationProvider.addToHistory([episodeId]);
          }
        }
      },
      assignScheduleResponse: assign(({ chapters, episode }, { data }) => ({
        ...data,
        chapters,
        episode,
      })),
      assignChapterResponse: assign((_, { data }) => ({
        chapters: data?.chapters,
      })),
      // Perhaps a little too generic, this is only used to assign source aka the URL to play from
      assignRewindContext: assign((ctx, { data }) => {
        return { ...ctx, ...data };
      }),
      // Gets new metadata from AVPlayer/Exoplayer every second, then merge that with old metadata we may need,
      // update ctx.metadata, then in "onTransition" we send metadata to PlayerStateConnector then to Recoil atoms
      assignMetadata: assign((ctx, e) => {
        const { metadata, episode, standaloneChapter, item, logger } = ctx;

        const retVal: Partial<IRewindStationMachineContext> = {};

        const entity = standaloneChapter ?? episode;

        const newMetadata = 'metadata' in e ? e.metadata : undefined;

        // Takes care of edgecase where data for title is incorrect from manifest
        if (newMetadata?.songOrShow) {
          newMetadata.songOrShow = '';
          newMetadata.artist = '';
        }

        retVal.metadata = mergeMetadata({
          existingMetadata: metadata,
          newMetadata,
          logger,
          entity,
          item,
        });

        retVal.contentType =
          entity?.isRewindable() ?? true ? EContentType.Rewind : EContentType.RewindNoReplay;

        return retVal;
      }),
      // Exit rewind mode and go back to live
      // TODO: [CCS-986]: Potentially need to accommodate StandaloneChapter
      exitRewindMode: assign((ctx) => ({
        ...ctx,
        requestedOffset: undefined,
        episode: undefined,
        playLive: true,
      })),
      sendReportEnded: send({
        type: 'REPORT_ENDED',
      }),
      playWithOffset: (ctx) => {
        const { player, episode, playLive } = ctx;
        const loadParams = getLoadParams(ctx);
        // TODO: fix this by expanding the machine states to differentiate between initial play and resume
        // This is to prevent sending reportPlaybackRequested to Conviva on a RESUME
        const currentContentId = episode?.getId();
        if(episode !== undefined && ctx.lastPlayedContentId !== currentContentId) {
          reportPlaybackRequestedConviva(player, episode, playLive, ctx.credentialsProvider?.userToken);
          ctx.lastPlayedContentId = currentContentId;
          setContentInfoConviva(player, playLive, loadParams.url);
        }
        // Offset is already in the startTs of the audio URL
        return ctx.player.play();
      },
      saveResumePoint: assign((ctx): IRewindStationMachineContext => saveResumePoints(ctx, true)),
      // This is geared towards making less API requests as a result of Audacy going down due to nationwide FEMA alert.
      cacheChaptersOnPause: (ctx) => {
        const id = ctx.episode?.getId() ?? '';
        void setQuickPauseChapterCache(id);
      },
      // Save the resume point every few seconds locally aka without API calls
      // With this approach we can save often and never lose our place.
      // using ts-ignore because, otherwise it throws error on using constant RESUME_POINT_INTERVAL_MSEC from other file
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      saveLocalResumePoint: assign((context): IRewindStationMachineContext => {
        if (!context.timeSinceLastSavedResumePoint) {
          context.timeSinceLastSavedResumePoint = Date.now();
        }
        const currentTime = Date.now();
        const updateTime = context.timeSinceLastSavedResumePoint + RESUME_POINT_INTERVAL_MSEC;

        if (currentTime >= updateTime) {
          return saveResumePoints(context, false);
        }
        return context;
      }),
      // Reset the resume point to the beginning of the episode
      // Used when we finished an episode but don't want to resume from the end
      // Used to recover when GoLive playback fails as a workaround for a backend issue
      resetResumePoint: assign((ctx) => {
        const { elapsed, personalizationProvider, episode, standaloneChapter } = ctx;

        const entity = standaloneChapter ?? episode;

        if (!entity || !elapsed) {
          return ctx;
        }

        const resumePoint = entity.getStartTimeSeconds();

        void personalizationProvider.setPlayback(entity.getId(), resumePoint);

        ctx.elapsed = 0;
        return saveResumePoint(ctx, resumePoint);
      }),
      resetElapsed: assign((ctx) => {
        ctx.elapsed = 0;
        return ctx;
      }),
      // When we seek we need to update requestedOffset, elapsed, seekingInChapter etc.
      assignSeekTime: assign((ctx, { time }): IRewindStationMachineContext => {
        const {
          episode,
          standaloneChapter,
          analyticsProvider,
          elapsed,
          metadata: { isLive = false } = {},
          deviceInfoProvider,
        } = ctx;

        const entity = standaloneChapter ?? episode;

        const endTime = entity?.getEndTimeSeconds() || 0; 
        const startTime = entity?.getStartTimeSeconds() || 0;
        const now = Date.now() / 1000;
        const isLiveShow = startTime < now && endTime > now;

        if (isLiveShow && (startTime + time >= endTime)) {
          // this condition only happens when it is the live show and a user scrubs to the very end
          time -= 1;
        }

        const newCtx = performSeek(ctx, time);

        const audioRoute = deviceInfoProvider?.getAudioRoute();

        if (elapsed !== undefined && entity?.data.id) {
          analyticsProvider?.sendPlayerEvent({
            type: time > elapsed ? PlayerAction.FAST_FORWARD : PlayerAction.REWIND,
            contentId: entity?.data.id as string,
            rewindFlag: isLive,
            currentPosition: toIntegerOrUndefined(entity.getStartTimeSeconds() + elapsed),
            streamUrl: getFirstAudioSourceUrl(entity?.data.streamUrl as string | undefined),
            connectionType: audioRoute,
          });
        }
        return newCtx;
      }),
      // Every second or we get a time update from AVPlayer/Exoplayer/HLS.js
      // Here we use that update to update the "elapsed" value which affects the timestamps in the UI
      assignTime: assign(
        (ctx, { time }): IRewindStationMachineContext => ({
          ...ctx,
          ...mapPlayerTime(ctx, time),
        }),
      ),
      // Every second and whenever we scrub, we could be going into a different chapter and need to sync this
      assignCurrentChapterIndex: assign((ctx) => {
        const { currentChapterIndex } = getCurrentChapterIndex(ctx);
        return {
          currentChapterIndex,
        };
      }),
      // Before loading audio, we need to set the correct chapter index
      assignCurrentChapterIndexWithOffset: assign((ctx) => {
        const { currentChapterIndex } = getCurrentChapterIndex(ctx);

        return {
          currentChapterIndex,
          ...ctx,
        };
      }),
      // This allows us to override resume points and play live radio
      // We use this in CarPlay for a feature called "Jump back in live" as well as the "Go to live" button
      setPlayLive: assign((ctx: IRewindStationMachineContext) => ({
        ...ctx,
        playLive: true,
      })),
      // We use this to clear the setPlayLive flag immediately after we've used it
      clearPlayLive: assign((ctx: IRewindStationMachineContext) => ({
        ...ctx,
        playLive: false,
      })),
      handleRewindContentEnded(ctx: IRewindStationMachineContext): IRewindStationMachineContext {
        return {
          ...ctx,
          episode: undefined,
          playLive: true,
          requestedOffset: ctx.player.vodFeatureEnabled ? ctx.requestedOffset : undefined,
        };
      },
      // This generates a playable URL and feeds it into the native player with "player.load"
      loadAudio: assign((ctx) => {
        const loadParams = getLoadParams(ctx);

        ctx.player?.load({
          url: loadParams.url,
          isPodcast: loadParams.isPodcast,
          isAudioPreroll: false,
          liveContentUrl: '',
          autoplay: ctx.autoplay,
        });

        return {
          ...ctx,
          seekingInChapter: loadParams.seekingInChapter,
        };
      }),
      setIsLiveAndRequestedOffset: assign((ctx, e) => {
        return {
          ...ctx,
          playLive: e.playLive,
          seekingInChapter: false,
          requestedOffset: e.playlistNextStartTime,
        };
      }),
      // Analytics stop event
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      sendStopEvent: async ({
        analyticsProvider,
        episode,
        firstLoadTime,
        retryCount,
        errors,
        elapsed,
        contentType,
        metadata: { isLive = false } = {},
        deviceInfoProvider,
      }) => {
        const contentId = episode?.data?.id as string | undefined;
        if (!contentId) {
          return;
        }

        const audioRoute = deviceInfoProvider?.getAudioRoute();

        analyticsProvider.sendPlayerEvent({
          type: PlayerAction.STOP,
          contentId: contentId,
          rewindFlag: isLive,
          currentPosition: episode
            ? toIntegerOrUndefined(episode.getStartTimeSeconds() + (elapsed ?? 0))
            : undefined,
          streamUrl: getFirstAudioSourceUrl(episode?.data?.streamUrl as string | undefined),
          connectionType: audioRoute,
        });

        const { bandwidthEstimateBPS, historicBandwidthEstimateBPS, type } =
          (await deviceInfoProvider?.getNetworkInfo()) || {};

        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_PAUSE,
          eventDetails: {
            isLive,
            stationId: contentId,
            stationName: episode ? episode.getTitle() : undefined,
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            retryCount,
            bandwidthEstimateBPS,
            historicBandwidthEstimateBPS,
            connectivityType: type,
            isNativeErrorHandling: true,
          },
        });
        if (retryCount) {
          analyticsProvider.sendEventToListener({
            type: PlayerMetric.STREAM_RECONNECT_ABORTED,
            eventDetails: {
              isLive,
              stationId: contentId,
              stationName: episode ? episode.getTitle() : undefined,
              streamerType: contentType,
              uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
              retryCount,
              mostRecentError: errors?.[0],
              bandwidthEstimateBPS,
              historicBandwidthEstimateBPS,
              connectivityType: type,
              isNativeErrorHandling: true,
            },
          });
        }
      },
      // Analytics play event
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      sendPlayEvent: async ({
        analyticsProvider,
        episode,
        firstLoadTime,
        retryCount,
        contentType,
        elapsed,
        metadata: { isLive = false } = {},
        deviceInfoProvider,
        errors,
      }) => {
        const contentId = episode?.data?.id as string | undefined;
        if (!episode || !contentId) {
          return;
        }

        const audioRoute = deviceInfoProvider?.getAudioRoute();

        analyticsProvider.sendPlayerEvent({
          type: PlayerAction.PLAY,
          contentId,
          rewindFlag: isLive,
          currentPosition: episode
            ? toIntegerOrUndefined(episode.getStartTimeSeconds() + (elapsed ?? 0))
            : undefined,
          streamUrl: getFirstAudioSourceUrl(episode?.data.streamUrl),
          connectionType: audioRoute,
        });

        const { bandwidthEstimateBPS, historicBandwidthEstimateBPS, type } =
          (await deviceInfoProvider?.getNetworkInfo()) || {};

        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_PLAY,
          eventDetails: {
            isLive,
            stationId: contentId,
            stationName: episode ? episode.getTitle() : undefined,
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            retryCount,
            bandwidthEstimateBPS,
            historicBandwidthEstimateBPS,
            connectivityType: type,
            isNativeErrorHandling: true,
          },
        });
        if (retryCount) {
          analyticsProvider.sendEventToListener({
            type: PlayerMetric.STREAM_RECONNECT_SUCCESSFUL,
            eventDetails: {
              isLive,
              stationId: contentId,
              stationName: episode ? episode.getTitle() : undefined,
              streamerType: contentType,
              uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
              retryCount,
              mostRecentError: errors?.[0],
              bandwidthEstimateBPS,
              historicBandwidthEstimateBPS,
              connectivityType: type,
              isNativeErrorHandling: true,
            },
          });
        }
      },
      sendBufferingEvent: assign(() => {
        bufferingEventCounter.addEvent('end_buffering');
        // analyticsProvider.sendEventToListener({
        //   type: PlayerMetric.STREAM_BUFFERING,
        //   eventDetails: {
        //     isLive,
        //     stationId: item.getId(),
        //     streamerType: contentType,
        //     uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
        //     isNativeErrorHandling: true,
        //   },
        // });
        return {
          bufferStartTime: Date.now(),
        };
      }),
      sendResumeFromBufferingEvent: assign(() => {
        bufferingEventCounter.addEvent('end_buffering');
        //   analyticsProvider.sendEventToListener({
        //   type: PlayerMetric.STREAM_RESUME_FROM_BUFFERING,
        //     eventDetails: {
        //     isLive,
        //     stationId: item.getId(),
        //     streamerType: contentType,
        //     uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
        //     timeBufferingMs: bufferStartTime ? Date.now() - bufferStartTime : 0,
        //     isNativeErrorHandling: true,
        //   },
        // });
        return { bufferStartTime: undefined };
      }),
      sendNativePlaybackFailedEvent: (
        {
          analyticsProvider,
          contentType,
          item,
          firstLoadTime,
          retryCount,
          metadata: { isLive = false } = {},
        },
        event,
      ) => {
        AudacyLogger.info(
          `[${
            LoggerTopic.Streaming
          }]: RewindStationMachine: sendNativePlaybackFailedEvent event:\n${JSON.stringify(
            event,
            null,
            2,
          )}`,
        );

        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_PLAYBACK_FAILED,
          eventDetails: {
            isLive,
            stationId: item.getId(),
            stationName: item.getTitle(),
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            retryCount,
            error: {
              errorMessage: event?.errorMessage,
              errorType: event?.error,
            },
            isNativeErrorHandling: true,
          },
        });
      },
      sendPlaybackFailedToLoadEvent: (
        {
          analyticsProvider,
          contentType,
          item,
          firstLoadTime,
          retryCount,
          metadata: { isLive = false } = {},
        },
        event,
      ) => {
        AudacyLogger.info(
          `[${
            LoggerTopic.Streaming
          }]: RewindStationMachine: sendPlaybackFailedToLoadEvent event:\n${JSON.stringify(
            event,
            null,
            2,
          )}`,
        );

        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_FAILED_TO_LOAD,
          eventDetails: {
            isLive,
            stationId: item.getId(),
            stationName: item.getTitle(),
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            retryCount,
            error: {
              ...event,
            },
            isNativeErrorHandling: true,
          },
        });
      },
      ...DEFAULT_PLAYER_ACTIONS,
    },
  },
);

const selector: TStreamSelector<typeof rewindStationMachine> = async (
  dataObject,
  _dataProvider,
  logger,
) => {
  const playableDataObject = await getPlayableDataObject(dataObject);

  if (!playableDataObject) {
    return;
  }

  logger?.info('Using server-side rewind provider', dataObject);

  const { station, episode, streamType, standaloneChapter } = playableDataObject;

  return rewindStationMachine.withContext({
    ...rewindStationMachine.context,
    item: streamType === 'BROADCAST' ? station : dataObject, // the UI expects ctx.item to be the station not the episode in this case
    streamType,
    station,
    episode,
    standaloneChapter,
  });
};

// Exported for unit tests (same pattern as MP3RewindStationMachine) so
// services and guards can be inspected via `rewindStationMachine.options.*`.
export { rewindStationMachine };

export default selector;
